import React, { useState } from "react";
import {
  CalendarDays,
  PartyPopper,
  X,
  ArrowRight,
} from "lucide-react";

const Contact = () => {
  const [selectedOption, setSelectedOption] = useState(null);

  // =====================================================
  // OPENING HOURS
  // =====================================================

  const openingHours = [
    {
      day: "Monday",
      time: "11:00 AM - 10:00 PM",
    },
    {
      day: "Tuesday",
      time: "11:00 AM - 10:00 PM",
    },
    {
      day: "Wednesday",
      time: "11:00 AM - 10:00 PM",
    },
    {
      day: "Thursday",
      time: "11:00 AM - 10:00 PM",
    },
    {
      day: "Friday",
      time: "11:00 AM - 11:00 PM",
    },
    {
      day: "Saturday",
      time: "11:00 AM - 11:00 PM",
    },
    {
      day: "Sunday",
      time: "11:00 AM - 10:00 PM",
    },
  ];

  // =====================================================
  // CONTACT OPTIONS
  // =====================================================

  const contactOptions = [
    {
      id: "reservations",
      title: "Regular Reservations",
      description:
        "Book your table and enjoy an unforgettable dining experience.",
      icon: CalendarDays,
      name: "Reservations Team",
      phone: "+1 (548) 899-0050",
    },
    {
      id: "events",
      title: "Events, Party & Catering",
      description:
        "Celebrate your special moments with our events and catering team.",
      icon: PartyPopper,
      name: "Events & Catering Team",
      phone: "+1 (548) 333-3455",
    },
  ];

  // =====================================================
  // HANDLE CONTACT OPTION
  // =====================================================

  const handleOptionClick = (option) => {
    setSelectedOption(option);
  };

  return (
    <section className="w-full bg-white">

      {/* =====================================================
          MAIN CONTACT AREA
          OPENING HOURS LEFT + CONTACT BUTTONS RIGHT
      ====================================================== */}

      <div
        className="relative overflow-hidden bg-[#080808]"
        style={{
          backgroundImage: "url('/contact-bg.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/95" />

        {/* Teal Glow - Top Left */}
        <div className="absolute left-[-150px] top-[-150px] h-[400px] w-[400px] rounded-full bg-[#087f7d]/10 blur-[120px]" />

        {/* Teal Glow - Bottom Right */}
        <div className="absolute bottom-[-150px] right-[-150px] h-[400px] w-[400px] rounded-full bg-[#087f7d]/10 blur-[120px]" />

        {/* Decorative Circles */}
        <div className="absolute -right-40 -top-40 h-[500px] w-[500px] rounded-full border border-white/[0.04]" />

        <div className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full border border-white/[0.04]" />

        {/* =====================================================
            MAIN CONTAINER
        ====================================================== */}

        <div className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20 lg:px-10 lg:py-24">

          {/* =====================================================
              TWO COLUMN LAYOUT
          ====================================================== */}

          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center lg:gap-14">

            {/* =================================================
                LEFT SIDE - OPENING HOURS
            ================================================== */}

            <div className="w-full">

              {/* Heading */}
              <div className="mb-8 text-center lg:text-left">

                <h2 className="font-serif text-4xl font-medium tracking-wide text-white sm:text-5xl lg:text-6xl">
                  Opening Hours
                </h2>

                <p className="mx-auto mt-5 max-w-xl text-sm leading-7 text-white/50 sm:text-base lg:mx-0">
                  We're ready to welcome you. Join us for great food,
                  refreshing drinks, and unforgettable moments.
                </p>

              </div>

              {/* Opening Hours Card */}
              <div className="w-full">

                <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-4 shadow-2xl backdrop-blur-md sm:p-6 lg:p-8">

                  {/* Gold Top Line */}
                  <div className="absolute left-1/2 top-0 h-[2px] w-32 -translate-x-1/2 bg-[#c9a86a] shadow-[0_0_20px_rgba(201,168,106,0.5)]" />

                  {/* Inner Border */}
                  <div className="pointer-events-none absolute inset-3 rounded-xl border border-white/[0.04]" />

                  {/* Hours List */}
                  <div className="relative">

                    {openingHours.map((item, index) => (
                      <div
                        key={item.day}
                        className={`group relative flex items-center justify-between px-3 py-4 transition-all duration-300 sm:px-5 sm:py-5 ${
                          index !== openingHours.length - 1
                            ? "border-b border-dashed border-white/10"
                            : ""
                        }`}
                      >

                        {/* Hover Background */}
                        <div className="absolute inset-x-0 inset-y-1 scale-x-0 rounded-lg bg-[#c9a86a]/[0.05] transition-transform duration-300 group-hover:scale-x-100" />

                        {/* Day */}
                        <div className="relative flex items-center gap-3 sm:gap-4">

                          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#c9a86a] opacity-50 transition-all duration-300 group-hover:scale-150 group-hover:opacity-100 group-hover:shadow-[0_0_10px_#c9a86a]" />

                          <span className="text-sm font-medium text-white/80 transition-colors duration-300 group-hover:text-white sm:text-base">
                            {item.day}
                          </span>

                        </div>

                        {/* Time */}
                        <span className="relative text-right text-xs font-medium tracking-wide text-[#c9a86a] sm:text-sm md:text-base">
                          {item.time}
                        </span>

                      </div>
                    ))}

                  </div>

                </div>

              </div>

            </div>

            {/* =================================================
                RIGHT SIDE - CONTACT BUTTONS
            ================================================== */}

            <div className="w-full">

              {/* Section Header */}
              <div className="mb-8 text-center lg:text-left">

                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.35em] text-[#c9a86a]">
                  Get In Touch
                </p>

                <h2 className="font-serif text-4xl font-medium leading-tight text-white sm:text-5xl">
                  How Can We Help?
                </h2>

                <p className="mx-auto mt-5 max-w-xl text-sm leading-7 text-white/50 sm:text-base lg:mx-0">
                  Whether you're joining us for dinner, planning a group
                  gathering, or celebrating a special occasion, our team is
                  here to help.
                </p>

              </div>

              {/* =================================================
                  TWO BUTTONS
              ================================================== */}

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-1">

                {contactOptions.map((option) => {
                  const Icon = option.icon;

                  return (
                    <button
                      key={option.id}
                      onClick={() => handleOptionClick(option)}
                      className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-left shadow-xl backdrop-blur-md transition-all duration-500 hover:-translate-y-2 hover:border-[#c9a86a]/40 hover:bg-white/[0.07] hover:shadow-2xl sm:p-7"
                    >

                      {/* Hover Background */}
                      <div className="absolute inset-0 origin-bottom scale-y-0 bg-[#c9a86a] transition-transform duration-500 group-hover:scale-y-100" />

                      <div className="relative z-10">

                        {/* Icon + Arrow */}
                        <div className="mb-6 flex items-center justify-between">

                          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#087f7d]/15 transition-all duration-500 group-hover:bg-black/20">
                            <Icon className="h-6 w-6 text-[#087f7d] transition-colors duration-500 group-hover:text-white" />
                          </div>

                          <ArrowRight className="h-5 w-5 text-white/40 transition-all duration-500 group-hover:translate-x-1 group-hover:text-white" />

                        </div>

                        {/* Title */}
                        <h3 className="font-serif text-2xl font-medium text-white transition-colors duration-500 group-hover:text-black">
                          {option.title}
                        </h3>

                        {/* Description */}
                        <p className="mt-3 text-sm leading-6 text-white/50 transition-colors duration-500 group-hover:text-black/70">
                          {option.description}
                        </p>

                        {/* Contact Us */}
                        <div className="mt-7 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#c9a86a] transition-colors duration-500 group-hover:text-black">

                          Contact Us

                          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />

                        </div>

                      </div>

                    </button>
                  );
                })}

              </div>

            </div>

          </div>

        </div>

      </div>

      {/* =====================================================
          CONTACT POPUP
      ====================================================== */}

      {selectedOption && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 px-5 backdrop-blur-sm">

          <div className="relative w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl">

            {/* =================================================
                POPUP HEADER
            ================================================== */}

            <div className="relative bg-black px-7 py-8 text-white">

              {/* Close Button */}
              <button
                onClick={() => setSelectedOption(null)}
                className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 transition hover:bg-white/20"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Icon */}
              <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-white/20 bg-white/10">

                {React.createElement(selectedOption.icon, {
                  className: "h-6 w-6",
                })}

              </div>

              <p className="mb-2 text-xs uppercase tracking-[0.25em] text-white/60">
                Contact Us
              </p>

              <h3 className="font-serif text-3xl">
                {selectedOption.title}
              </h3>

            </div>

            {/* =================================================
                CONTACT DETAILS
            ================================================== */}

            <div className="px-7 py-8">

              <p className="mb-7 text-sm leading-6 text-gray-500">
              Our team is ready to assist you with your dining, events, parties, weddings, and special celebrations.
              </p>

              {/* Contact Person */}
              <div className="mb-5 rounded-xl border border-gray-100 bg-gray-50 p-4">

                <p className="mb-1 text-xs uppercase tracking-widest text-gray-400">
                  Contact Person
                </p>

                <p className="font-medium text-gray-900">
                  {selectedOption.name}
                </p>

              </div>

              {/* Phone Number */}
              <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">

                <p className="mb-1 text-xs uppercase tracking-widest text-gray-400">
                  Phone Number
                </p>

                <p className="font-medium text-gray-900">
                  {selectedOption.phone}
                </p>

              </div>

            </div>

          </div>

        </div>
      )}

    </section>
  );
};

export default Contact;